import { METRIC_SECTIONS, METRIC_META, HERO_METRICS, INVERSE_DELTA_METRICS } from "../config/businessMetrics";
import Avatar from "./ds/Avatar";
import Badge from "./ds/Badge";
import Button from "./ds/Button";
import Icon from "./ds/Icon";

const formatMoney = (n) => {
  const v = Number(n) || 0;
  return `₾${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const formatCount = (n) => (Number(n) || 0).toLocaleString();

const formatPercent = (n) => `${(Number(n) || 0).toFixed(1)}%`;

const formatMetricValue = (metric) => {
  if (metric?.unavailable) return "—";
  if (metric?.value == null) return "—";
  if (metric.format === "money") return formatMoney(metric.value);
  if (metric.format === "percent") return formatPercent(metric.value);
  return formatCount(metric.value);
};

const formatShortDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const DeltaBadge = ({ metric, snapshot, inverse, t }) => {
  if (metric?.unavailable) return <Badge size="s">{t("mintSpaceSettings.metricComingSoon")}</Badge>;
  if (snapshot) return <Badge size="s">{t("mintSpaceSettings.metricLiveSnapshot")}</Badge>;
  const changePct = metric?.changePct;
  if (changePct == null || Number.isNaN(changePct)) {
    return metric?.value ? null : <Badge size="s">0%</Badge>;
  }
  if (Math.abs(changePct) < 0.05) return <Badge size="s">0%</Badge>;
  const up = changePct > 0;
  return (
    <span title={t("mintSpaceSettings.metricVsPrior")}>
      <Badge size="s" tone={(inverse ? !up : up) ? "success" : "danger"}>
        {`${up ? "+" : ""}${changePct.toFixed(1)}%`}
      </Badge>
    </span>
  );
};

/** The trend under a figure. Nothing to draw yet is Figma's flat line. */
const Trend = ({ points }) => {
  const values = (points || []).map((p) => Number(p.value) || 0);
  if (values.length < 2 || !values.some(Boolean)) {
    return <span className="h-[2px] w-full rounded-full bg-accent-tinted" aria-hidden="true" />;
  }
  const w = 120;
  const h = 36;
  const max = Math.max(1, ...values);
  const step = w / (values.length - 1);
  const coords = values.map((v, i) => [i * step, h - 4 - ((h - 8) * v) / max]);
  const line = coords.reduce((acc, [x, y], i) => (i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`), "");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-9 w-full" preserveAspectRatio="none" aria-hidden>
      <path d={`${line} L ${w} ${h} L 0 ${h} Z`} fill="var(--color-accent-tinted)" />
      <path d={line} fill="none" stroke="var(--color-accent-default)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

/** The Footnote line under a figure: what the figure is made of, else last period's. */
const metricNote = (metricId, metric, metrics, t) => {
  const ctx = metric?.context;
  if (metricId === "arpu" && ctx?.payingCustomers != null) {
    return `${ctx.payingCustomers.toLocaleString()} ${t("mintSpaceSettings.metricPayingCustomers")}`;
  }
  const trialing = metricId === "mrr" ? metrics.paidActiveMembers?.context?.trialing : ctx?.trialing;
  if ((metricId === "paidActiveMembers" || metricId === "mrr") && trialing != null) {
    return `+${trialing} ${t("mintSpaceSettings.kpiTrialing").toLowerCase()}`;
  }
  if (metricId === "refundRate" && ctx?.orders != null) {
    return `${ctx.refundEvents}/${ctx.orders} ${t("mintSpaceSettings.metricReceipts")}`;
  }
  if (metricId === "churnRate" && ctx?.churnedUsers != null) {
    return `${ctx.churnedUsers} ${t("mintSpaceSettings.metricChurnedUsers")} · ${ctx.windowDays}d`;
  }
  if (metric?.unavailable) return METRIC_META[metricId]?.tipKey ? t(METRIC_META[metricId].tipKey) : null;
  if (metric?.snapshot || metric?.priorValue == null) return null;
  return `${t("mintSpaceSettings.metricWas")} ${formatMetricValue({ ...metric, value: metric.priorValue })}`;
};

const MetricBox = ({ metricId, metrics, series, t, onOpenGlossary }) => {
  const meta = METRIC_META[metricId];
  if (!meta) return null;
  const metric = metrics[metricId];
  const snapshot = metric?.snapshot || meta.snapshot;
  return (
    <article
      className="flex min-w-0 flex-col gap-space-8 rounded-radius-lg bg-bg-raised p-space-16"
      title={meta.tipKey ? t(meta.tipKey) : undefined}
    >
      <div className="flex min-h-[20px] items-center justify-between gap-space-8">
        <h4 className="min-w-0 text-caps-label uppercase text-label-secondary">{t(meta.labelKey)}</h4>
        <DeltaBadge metric={metric} snapshot={snapshot} inverse={INVERSE_DELTA_METRICS.has(metricId)} t={t} />
      </div>
      <p className="truncate text-title-1 text-label-primary">{formatMetricValue(metric)}</p>
      <p className="min-h-[20px] text-footnote text-label-tertiary">{metricNote(metricId, metric, metrics, t)}</p>
      <Trend points={metric?.unavailable || snapshot ? null : series[meta.seriesKey || metricId]} />
      {meta.tipKey && meta.glossarySlug && (
        <Button
          variant="plain"
          size="s"
          icon={<Icon name="arrow-right" size={16} />}
          className="self-start"
          onClick={() => onOpenGlossary?.(meta.glossarySlug)}
        >
          {t("mintSpaceSettings.metricLearnMore")}
        </Button>
      )}
    </article>
  );
};

const SectionTitle = ({ children }) => <h3 className="text-caps-label uppercase text-accent-text">{children}</h3>;

const TopList = ({ title, rows, value }) => (
  <div className="flex min-w-0 flex-col gap-space-8 rounded-radius-lg bg-bg-raised p-space-16">
    <h4 className="text-headline text-label-primary">{title}</h4>
    <ul className="flex flex-col gap-space-12">
      {rows.map((row) => (
        <li key={row._id} className="flex items-center gap-space-12">
          <Avatar size="m" name={row.name} initials={(row.name || "?")[0]} />
          <span className="min-w-0 flex-1 truncate text-subheadline-semibold text-label-primary">{row.name || "—"}</span>
          <span className="shrink-0 text-subheadline-semibold text-label-primary">{value(row)}</span>
        </li>
      ))}
    </ul>
  </div>
);

/** Figma D2: figures in groups, At a glance first. */
export default function BusinessAnalyticsDashboard({ t, data, onOpenGlossary }) {
  const metrics = data?.metrics || {};
  const series = data?.series || {};
  const range = data?.range;
  const priorRange = data?.priorRange;
  const box = (metricId) => (
    <MetricBox key={metricId} metricId={metricId} metrics={metrics} series={series} t={t} onOpenGlossary={onOpenGlossary} />
  );
  const row = "grid grid-cols-1 gap-space-16 min-[600px]:gap-space-20 lg:gap-space-24";

  return (
    <>
      <SectionTitle>{t("mintSpaceSettings.dashboardHeroTitle")}</SectionTitle>
      <div className={`${row} min-[600px]:grid-cols-2 min-[1700px]:grid-cols-4`}>{HERO_METRICS.map(box)}</div>

      {METRIC_SECTIONS.map((section) => (
        <section key={section.id} className="contents">
          <SectionTitle>{t(section.titleKey)}</SectionTitle>
          <div className={`${row} min-[600px]:grid-cols-3`}>{section.metrics.map(box)}</div>
        </section>
      ))}

      {(data?.topProducts?.length > 0 || data?.topCampaigns?.length > 0) && (
        <section className={`${row} items-start min-[600px]:grid-cols-2`}>
          {data.topProducts?.length > 0 && (
            <TopList
              title={t("mintSpaceSettings.topProducts")}
              rows={data.topProducts}
              value={(p) => formatMoney((p.gross || 0) - (p.refunded || 0))}
            />
          )}
          {data.topCampaigns?.length > 0 && (
            <TopList title={t("mintSpaceSettings.topCampaigns")} rows={data.topCampaigns} value={(c) => formatMoney(c.paidInRange)} />
          )}
        </section>
      )}

      {range?.from && priorRange?.from && (
        <p className="text-center text-footnote text-label-tertiary">
          {t("mintSpaceSettings.metricPeriodCompare")
            .replace("{from}", formatShortDate(range.from))
            .replace("{to}", formatShortDate(range.to))
            .replace("{priorFrom}", formatShortDate(priorRange.from))
            .replace("{priorTo}", formatShortDate(priorRange.to))}
        </p>
      )}
    </>
  );
}
